import { createContext, PropsWithChildren, useCallback, useReducer, useRef } from 'react';

import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import SasVerificationNotificationsManager from 'contexts/MatrixDeviceVerificationContext/sasVerification/useSasVerificationNotificationsManager';
import useIsFeatureEnabled from 'hooks/useIsFeatureEnabled';
import { FeatureFlag } from 'utils/Constants';

import { notificationActions, notificationsReducer, Notification } from './utils';

// ─── Tiny read-ID store ───────────────────────────────────────────────────────
// Persists only the set of notification IDs the user has marked as read.
// No notification content is ever stored, so there are no stale-content or
// icon-mapping problems on reload. Feeders still own the full notification UI;
// we just restore the isRead flag when they re-add an already-read item.

const readIdsKey = (userId: string) => `meedio:notification-read:${userId}`;

const loadReadIds = (userId: string): Set<string> => {
  try {
    const raw = localStorage.getItem(readIdsKey(userId));
    if (!raw) return new Set();
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? new Set<string>(parsed as string[]) : new Set();
  } catch {
    return new Set();
  }
};

const saveReadIds = (userId: string, ids: Set<string>): void => {
  try {
    localStorage.setItem(readIdsKey(userId), JSON.stringify([...ids]));
  } catch {
    // storage full or unavailable — silently ignore
  }
};

// ─── Context ──────────────────────────────────────────────────────────────────

type NotificationsContextType = {
  notificationsState: Notification[];
  addNotification: (options: Notification) => void;
  removeNotification: (id: string) => void;
  removeNotificationsByPrefix: (prefix: string) => void;
  editNotification: (id: string, options: Omit<Notification, 'id'>) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  clearAllNotifications: () => void;
  isNotificationRead: (id: string) => boolean;
};

export const NotificationsContext = createContext<NotificationsContextType | null>(null);

const NotificationsProvider = ({ children }: PropsWithChildren) => {
  const isCrossSigningEnabled = useIsFeatureEnabled(FeatureFlag.FE_ENABLE_CROSS_SIGNING);
  // requireDefinedMatrixClient: false — provider may render before the client is ready.
  const { matrixClient } = useMatrixContext({ requireDefinedMatrixClient: false });
  const userId = matrixClient?.getUserId() ?? null;

  const [notificationsState, dispatch] = useReducer(notificationsReducer, []);

  // Seeded synchronously from localStorage during render (safe — localStorage is sync).
  // Re-seeds automatically when userId changes (account switch).
  const readIdsRef = useRef<Set<string>>(new Set());
  const readIdsUserRef = useRef<string | null>(null);
  if (userId && userId !== readIdsUserRef.current) {
    readIdsRef.current = loadReadIds(userId);
    readIdsUserRef.current = userId;
  }

  // Stable ref so markAllAsRead can always read the latest notifications list
  // without putting notificationsState in its useCallback deps.
  const notificationsStateRef = useRef(notificationsState);
  notificationsStateRef.current = notificationsState;

  // Wraps addNotification: if the feeder re-adds an already-read item, restore isRead.
  const addNotification = useCallback((options: Notification) => {
    const isRead = readIdsRef.current.has(options.id) || options.isRead;
    dispatch(notificationActions.addNotification({ ...options, isRead }));
  }, []); // readIdsRef is a stable ref — no deps needed

  const removeNotification = useCallback((id: string) => dispatch(notificationActions.removeNotification(id)), []);
  const removeNotificationsByPrefix = useCallback(
    (prefix: string) => dispatch(notificationActions.removeNotificationsByPrefix(prefix)),
    []
  );
  const editNotification = useCallback(
    (id: string, options: Omit<Notification, 'id'>) => dispatch(notificationActions.editNotification(id, options)),
    []
  );

  const markAsRead = useCallback(
    (id: string) => {
      dispatch(notificationActions.markAsRead(id));
      if (userId) {
        readIdsRef.current.add(id);
        saveReadIds(userId, readIdsRef.current);
      }
    },
    [userId]
  );

  const markAllAsRead = useCallback(() => {
    dispatch(notificationActions.markAllAsRead());
    if (userId) {
      notificationsStateRef.current.forEach((n) => readIdsRef.current.add(n.id));
      saveReadIds(userId, readIdsRef.current);
    }
  }, [userId]); // notificationsStateRef is stable — only userId is a real dep

  const clearAllNotifications = useCallback(() => dispatch(notificationActions.clearAllNotifications()), []);

  const isNotificationRead = useCallback((id: string) => readIdsRef.current.has(id), []);

  return (
    <NotificationsContext.Provider
      value={{
        notificationsState,
        addNotification,
        removeNotification,
        removeNotificationsByPrefix,
        editNotification,
        markAsRead,
        markAllAsRead,
        clearAllNotifications,
        isNotificationRead,
      }}
    >
      {isCrossSigningEnabled && <SasVerificationNotificationsManager />}
      {children}
    </NotificationsContext.Provider>
  );
};

export default NotificationsProvider;
